import { useCallback, useEffect, useRef, useState } from 'react';

import { getSpeechStatus, transcribeSpeechAudio } from '../api';
import { formatApiError } from '../utils/apiErrorMessage';
import {
  makePendingPacienteId,
  titleCaseNombre,
} from '../utils/citasHelpers';
import { extensionForMime, startVoiceRecording } from '../utils/voiceRecorder';

function norm(s) {
  return (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[¿?¡!.,;:…"«»]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Corrige errores frecuentes de Whisper (tiny) en español. */
export function softenVoiceText(raw) {
  let t = norm(raw);
  if (!t) return '';

  const replacements = [
    [/\bnevas?\b/g, 'nueva'],
    [/\bnev a\b/g, 'nueva'],
    [/\bnueba\b/g, 'nueva'],
    [/\bhittos?\b/g, 'citas'],
    [/\bhitos?\b/g, 'citas'],
    [/\bsitas?\b/g, 'citas'],
    [/\bcitta\b/g, 'cita'],
    [/\bcitar\b/g, 'cita'],
    [/\ba gregar\b/g, 'agregar'],
    // Whisper confunde a menudo agendar ↔ agrandar / agenda
    [/\bagrandar\b/g, 'agendar'],
    [/\ba\s*grandar\b/g, 'agendar'],
    [/\bagranda\s*r\b/g, 'agendar'],
    // No convertir «agenda de hoy» (consulta) en «agendar»
    [/\bagenda\s+de\s+hoy\b/g, 'cita de hoy'],
    [/\bagenda\s*r\b/g, 'agendar'],
    [/\bagenda\b/g, 'agendar'],
    [/\ba\s*gendar\b/g, 'agendar'],
    [/\bagendar\b/g, 'agendar'],
    [/\bquiero\s+agendar\b/g, 'agendar'],
    [/\bvamos\s+a\s+agendar\b/g, 'agendar'],
    [/\bprogramar\b/g, 'programar'],
    [/\bprograma r\b/g, 'programar'],
    [/\bpacientes?\b/g, 'paciente'],
    [/\bpasiente\b/g, 'paciente'],
    [/\bpasientes\b/g, 'paciente'],
    [/\bpersona\b/g, 'persona'],
    [/\bmotti?vo\b/g, 'motivo'],
    [/\bmotibo\b/g, 'motivo'],
    [/\bguarda r\b/g, 'guardar'],
    [/\bguadar\b/g, 'guardar'],
    [/\bguardad\b/g, 'guardar'],
    [/\bconfirma r\b/g, 'confirmar'],
    [/\bora inicio\b/g, 'hora inicio'],
    [/\bora fin\b/g, 'hora fin'],
    [/\bmiercoles\b/g, 'miercoles'],
    [/\bmiercole\b/g, 'miercoles'],
    [/\bsabados?\b/g, 'sabado'],
    [/\bvienes\b/g, 'viernes'],
    [/\bvierne\b/g, 'viernes'],
    [/\bjuebe[s]?\b/g, 'jueves'],
    [/\bjueve[s]?\b/g, 'jueves'],
    [/\bhueves\b/g, 'jueves'],
    [/\blune[s]?\b/g, 'lunes'],
    [/\bmartes\b/g, 'martes'],
    [/\bpara el dia\b/g, 'para el'],
    // Whisper: «dame» → «de me» / «deme» (la frase larga primero)
    [/\bde\s+me\s+la\b/g, 'dame la'],
    [/\bde\s+me\b/g, 'dame'],
    [/\bdeme\b/g, 'dame'],
    [/\bdime\s+la\b/g, 'dime la'],
  ];
  for (const [re, to] of replacements) t = t.replace(re, to);

  t = t.replace(/\bcitas\b/g, 'cita');
  return repairVoiceTokens(t.replace(/\s+/g, ' ').trim());
}

const DAY_STOP = new Set(['hoy', 'manana', 'cita', 'citas', 'para', 'hora', 'horario', 'desde', 'entre']);

/** Fuzzy post-norm (después de definir closeWord). */
function repairVoiceTokens(t) {
  if (!t) return '';
  return t.split(/\s+/).map((w) => {
    if (DAY_STOP.has(w)) return w;
    if (w.length >= 6 && (closeWord(w, 'agendar', 2) || closeWord(w, 'agrandar', 2))) return 'agendar';
    if (w === 'sita' || w === 'citta' || w === 'hita' || w === 'citar') return 'cita';
    for (const day of ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado']) {
      // Evitar que palabras cortas (hoy, cita…) se conviertan en un día.
      if (w.length >= 5 && closeWord(w, day, 1)) return day;
    }
    return w;
  }).join(' ');
}

function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i += 1) dp[i][0] = i;
  for (let j = 0; j <= n; j += 1) dp[0][j] = j;
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

function closeWord(a, b, maxDist = 2) {
  if (!a || !b) return false;
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > maxDist) return false;
  return levenshtein(a, b) <= maxDist;
}

function hasCloseToken(toks, candidates, maxDist = 2) {
  return toks.some((t) => candidates.some((c) => closeWord(t, c, maxDist)));
}

const WORD_HOUR = {
  cero: 0, una: 1, un: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7,
  ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12, trece: 13, catorce: 14, quince: 15,
  dieciseis: 16, diecisiete: 17, dieciocho: 18, diecinueve: 19, veinte: 20,
  veintiuno: 21, veintidos: 22, veintitres: 23,
};

const WORD_MIN = {
  cero: 0, diez: 10, quince: 15, veinte: 20, treinta: 30, cuarenta: 40, cincuenta: 50,
};

const WEEKDAY = {
  domingo: 0,
  lunes: 1,
  martes: 2,
  miercoles: 3,
  jueves: 4,
  viernes: 5,
  sabado: 6,
};

function parseClockToken(raw) {
  const soft = softenVoiceText(raw);
  if (!soft) return null;

  const colon = soft.match(/(\d{1,2})\s*[.:]\s*(\d{2})/);
  if (colon) {
    return { h: parseInt(colon[1], 10), m: parseInt(colon[2], 10) };
  }

  const digit = soft.match(/\b(\d{1,2})\b/);
  if (digit) {
    let m = 0;
    if (/\bmedia\b/.test(soft)) m = 30;
    else if (/\bcuarto\b/.test(soft)) m = 15;
    else {
      const mm = soft.match(/\b(cero|diez|quince|veinte|treinta|cuarenta|cincuenta)\b/);
      if (mm) m = WORD_MIN[mm[1]] ?? 0;
      else {
        const m2 = soft.match(/\b(\d{1,2})\s+(\d{2})\b/);
        if (m2 && m2[1] === digit[1]) m = parseInt(m2[2], 10);
      }
    }
    return { h: parseInt(digit[1], 10), m };
  }

  const spoken = soft.match(
    /\b(cero|una|un|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|trece|catorce|quince|dieciseis|diecisiete|dieciocho|diecinueve|veinte|veintiuno|veintidos|veintitres)\b/,
  );
  if (spoken) {
    let m = 0;
    if (/\bmedia\b/.test(soft)) m = 30;
    else if (/\bcuarto\b/.test(soft)) m = 15;
    else {
      const mm = soft.match(/\b(cero|diez|quince|veinte|treinta|cuarenta|cincuenta)\b/);
      if (mm) m = WORD_MIN[mm[1]] ?? 0;
    }
    return { h: WORD_HOUR[spoken[1]], m };
  }
  return null;
}

function parseTimePair(raw) {
  const clock = parseClockToken(raw);
  if (!clock) return null;
  return [String(clock.h), String(clock.m)];
}

/** Horario clínico: 1–6 sin “mañana” → tarde (13–18). */
function toClinicHour(h, softAround = '') {
  let hour = h;
  if (hour > 23) hour = 23;
  const morning = /\b(manana|am|a\.?\s*m\.?)\b/.test(softAround);
  const afternoon = /\b(tarde|pm|p\.?\s*m\.?|noche)\b/.test(softAround);
  if (afternoon && hour >= 1 && hour <= 11) hour += 12;
  else if (!morning && hour >= 1 && hour <= 6) hour += 12;
  return Math.min(23, hour);
}

function formatHm(h, m) {
  return `${String(h).padStart(2, '0')}:${String(Math.min(59, m || 0)).padStart(2, '0')}`;
}

function toLocalDayString(d) {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

function formatDayEs(d) {
  return d.toLocaleDateString('es-EC', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });
}

function nextWeekdayDate(targetDow, { nextWeek = false, from = new Date() } = {}) {
  const d = new Date(from);
  d.setHours(12, 0, 0, 0);
  const cur = d.getDay();
  let add = (targetDow - cur + 7) % 7;
  if (nextWeek && add === 0) add = 7;
  d.setDate(d.getDate() + add);
  return d;
}

function matchPaciente(spoken, pacientes) {
  const q = softenVoiceText(spoken);
  if (!q || !pacientes?.length) return null;
  const stop = new Set([
    'el', 'la', 'los', 'las', 'un', 'una', 'de', 'del', 'para', 'con', 'por',
    'cita', 'paciente', 'persona', 'nombre', 'señor', 'senor', 'señora', 'senora',
  ]);
  const words = q.split(/\s+/).filter((w) => w.length > 1 && !stop.has(w));
  if (!words.length) return null;

  let best = null;
  let bestScore = 0;
  for (const p of pacientes) {
    const n = softenVoiceText(p.nombre);
    if (!n) continue;
    if (n === q || n.includes(q) || q.includes(n)) {
      return { id: p.id, nombre: p.nombre };
    }
    let score = 0;
    const nw = n.split(/\s+/);
    for (const w of words) {
      if (n.includes(w) || nw.some((x) => closeWord(x, w, 1))) score += w.length;
    }
    if (score > bestScore) {
      bestScore = score;
      best = { id: p.id, nombre: p.nombre };
    }
  }
  return bestScore >= 3 ? best : null;
}

function matchEquipo(spoken, equipo) {
  const q = softenVoiceText(spoken);
  if (!q || !equipo?.length) return null;
  for (const m of equipo) {
    const n = softenVoiceText(m.full_name || '');
    if (n && (n.includes(q) || q.includes(n))) return String(m.customer_id);
  }
  const words = q.split(/\s+/).filter((w) => w.length > 2);
  for (const m of equipo) {
    const n = softenVoiceText(m.full_name || '');
    for (const w of words) {
      if (n.includes(w) || n.split(/\s+/).some((nw) => closeWord(nw, w, 1))) {
        return String(m.customer_id);
      }
    }
  }
  return null;
}

function isOpenModalCommand(sIn) {
  const soft = softenVoiceText(sIn);
  if (!soft) return false;
  // Oraciones largas las maneja el NLU (no solo abrir vacío).
  if (soft.split(/\s+/).length > 4) return false;
  const toks = soft.split(/\s+/);

  if (
    /^(nueva|agregar|crear|agendar|abrir)(\s+(una|la))?\s+(nueva\s+)?cita$/.test(soft)
    || /^(quiero\s+)?(abrir|agendar|crear|agregar)\s+(una\s+)?(nueva\s+)?cita$/.test(soft)
    || /^agendar\s+nueva\s+cita$/.test(soft)
    || /^registrar\s+(una\s+)?(nueva\s+)?cita$/.test(soft)
  ) return true;

  const hasNueva = hasCloseToken(toks, ['nueva', 'nuevas', 'nuevo', 'nuevos'], 2);
  const hasCita = hasCloseToken(toks, ['cita', 'citas'], 2);
  const hasAction = hasCloseToken(toks, ['agregar', 'crear', 'agendar', 'abrir', 'registrar', 'hacer'], 2);
  if (hasNueva && hasCita) return true;
  if (hasAction && hasCita) return true;
  if (toks.length <= 3 && hasCita && (hasNueva || hasAction || toks.includes('una'))) return true;

  return false;
}

function isCloseCommand(sIn) {
  const soft = softenVoiceText(sIn);
  return (
    /^(cerrar|cancelar)(\s+(modal|ventana|formulario))?$/.test(soft)
    || /^cierra(\s+(la\s+)?ventana)?$/.test(soft)
  );
}

function isSaveCommand(sIn) {
  const soft = softenVoiceText(sIn);
  return (
    /^(guardar|confirmar)(\s+(la\s+)?(cita|informacion))?$/.test(soft)
    || /^guardar\s+informacion$/.test(soft)
    || soft === 'listo'
    || soft === 'terminar'
  );
}

function isConfirmYes(sIn) {
  const soft = softenVoiceText(sIn);
  return (
    /^(si|ok|okay|dale|claro|perfecto|confirmar|confirmo|guardar|de acuerdo)(\s+(por\s+favor|ahora|la\s+cita))?$/.test(soft)
    || /^(si|ok)?,?\s*(esta\s+)?correcto(\s+agendar)?$/.test(soft)
    || /^esta\s+correcto(\s+agendar)?$/.test(soft)
    || /^correcto(\s+agendar)?$/.test(soft)
    || /^agendar(\s+(ahora|la\s+cita))?$/.test(soft)
    || /^si\s+agendar$/.test(soft)
  );
}

function isConfirmNo(sIn) {
  const soft = softenVoiceText(sIn);
  return /^(no|cancelar|cancela|mejor\s+no|no\s+agendar|incorrecto)$/.test(soft);
}

const DAY_WORDS = 'lunes|martes|miercoles|jueves|viernes|sabado|domingo';

function extractPatientPhrase(soft) {
  // “para el jueves” NO es un paciente.
  if (new RegExp(`\\bpara\\s+(?:el|la|los|las|un|una)?\\s*(?:${DAY_WORDS}|hoy|manana)\\b`).test(soft)) {
    /* weekday frase — no paciente */
  } else {
    const patterns = [
      /\b(?:para\s+(?:la\s+|el\s+)?(?:persona|paciente)|paciente|persona|a\s+nombre\s+de|con\s+(?:el\s+|la\s+)?paciente)\s+(.+?)(?=\s+(?:el|la|los|las|un|una|este|esta|proximo|proxima|para|por|de\s+\d|de\s+las|desde|entre|el\s+(?:lunes|martes|miercoles|jueves|viernes|sabado|domingo)|(?:lunes|martes|miercoles|jueves|viernes|sabado|domingo)|manana|hoy|motivo)\b|$)/,
      new RegExp(
        `\\bpara\\s+(?!la\\s+persona\\b|el\\s+paciente\\b|la\\s+paciente\\b|(?:el|la|los|las|un|una)\\s+(?:${DAY_WORDS})\\b|(?:${DAY_WORDS})\\b)(.+?)(?=\\s+(?:el|la|un|una|este|esta|proximo|proxima|de\\s+\\d|de\\s+las|desde|el\\s+(?:${DAY_WORDS})|(?:${DAY_WORDS})|manana|hoy|motivo)\\b|$)`,
      ),
    ];
    for (const re of patterns) {
      const m = soft.match(re);
      if (m?.[1]) {
        let name = m[1]
          .replace(/\b(un|una|el|la|los|las)\b/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        // Evitar capturar días como si fueran nombre
        if (new RegExp(`^(?:${DAY_WORDS})$`).test(name)) name = '';
        if (name.length >= 2) return name;
      }
    }
  }
  return null;
}

function extractWeekdayDate(soft) {
  // «hoy» / «mañana» primero: no deben confundirse con fuzzy de días.
  if (/\bhoy\b/.test(soft)) {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  }
  if (/\bmanana\b/.test(soft)) {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(12, 0, 0, 0);
    return d;
  }
  const nextWeek = /\b(proximo|proxima|que\s+viene)\b/.test(soft);
  const toks = soft.split(/\s+/).filter((t) => !DAY_STOP.has(t) && t.length >= 5);
  for (const [name, dow] of Object.entries(WEEKDAY)) {
    if (new RegExp(`\\b${name}\\b`).test(soft)) {
      return nextWeekdayDate(dow, { nextWeek });
    }
    // Fuzzy estricto: “juebe”, “vierne”, etc. (no “hoy” ni “cita”)
    if (toks.some((t) => closeWord(t, name, 1))) {
      return nextWeekdayDate(dow, { nextWeek });
    }
  }
  return null;
}

function hasExplicitDay(soft) {
  return extractWeekdayDate(soft) != null;
}

function extractTimeRange(soft) {
  // de 2 a 3 / de las 14 a las 15 / desde 10:30 hasta 11:00 / entre 2 y 3
  const rangeRe = /\b(?:de(?:\s+las)?|desde|entre)\s+(.+?)\s+(?:a(?:\s+las)?|hasta|y)\s+(.+?)(?=\s+(?:para|con|motivo|el|la|paciente|persona)|$)/;
  let m = soft.match(rangeRe);
  if (!m) {
    m = soft.match(/\b(?:a\s+las|a\s+la|desde\s+las)\s+(\d{1,2}(?:\s*[.:]\s*\d{2})?|\w+)(?:\s*(?:a|hasta)\s+(?:las?\s*)?(\d{1,2}(?:\s*[.:]\s*\d{2})?|\w+))?/);
  }
  if (m) {
    const start = parseClockToken(m[1]);
    const end = m[2] ? parseClockToken(m[2]) : null;
    if (start) {
      const hs = toClinicHour(start.h, soft);
      const he = end ? toClinicHour(end.h, soft) : Math.min(23, hs + 1);
      const ms = start.m || 0;
      const me = end ? (end.m || 0) : ms;
      return { start: formatHm(hs, ms), end: formatHm(he, me) };
    }
  }

  // hora inicio/fin estilo comando embebido
  const solo = soft.match(/\b(?:a\s+las|hora)\s+(\d{1,2}(?:\s*[.:]\s*\d{2})?)/);
  if (solo) {
    const start = parseClockToken(solo[1]);
    if (start) {
      const hs = toClinicHour(start.h, soft);
      return { start: formatHm(hs, start.m || 0), end: formatHm(Math.min(23, hs + 1), start.m || 0) };
    }
  }
  return null;
}

function extractMotivo(soft) {
  const m = soft.match(/\b(?:motivo|asunto|por|para\s+(?:una\s+)?)((?:limpieza|control|revision|consulta|ortodoncia|endodoncia|extraccion|blanqueamiento|urgencia)(?:\s+\w+){0,3})/);
  if (m) return m[1].trim();
  return '';
}

/** Consulta de agenda («dame las citas de hoy»), no agendar. */
export function isTodayCitasQuery(softIn) {
  const soft = softenVoiceText(softIn);
  if (!soft) return false;
  // Acciones claras de crear cita → no es consulta
  if (/\b(agendar|agregar|crear|programar|reservar|sacar|nueva|registrar)\b/.test(soft)) {
    return false;
  }
  return (
    /\b(cuantas|cuantos)\b.*\bcita\b/.test(soft)
    || /\b(dame|dime|mostrar|mostrame|ver|listar|decir)\b.*\bcita\b/.test(soft)
    || /\bcita\b.*\b(de\s+)?hoy\b/.test(soft)
    || /\bhoy\s+(cuantas\s+)?cita\b/.test(soft)
  );
}

function hasScheduleIntent(soft) {
  if (isTodayCitasQuery(soft)) return false;
  const toks = soft.split(/\s+/);
  const hasCita = hasCloseToken(toks, ['cita'], 2);
  const hasAction = hasCloseToken(
    toks,
    ['agendar', 'agregar', 'crear', 'programar', 'reservar', 'sacar', 'poner', 'registrar', 'nueva', 'agrandar'],
    2,
  );
  const day = hasExplicitDay(soft);
  if (hasCita && hasAction) return true;
  // «agendar … para el jueves» aunque falte “cita”
  if (hasAction && day) return true;
  // «cita para el jueves» / «cita el viernes» (exige “para” o franja; no basta “cita … hoy”)
  if (hasCita && day && (/\bpara\b/.test(soft) || extractTimeRange(soft))) return true;
  if (/\bpara\s+(la\s+)?(persona|paciente)\b/.test(soft) && extractTimeRange(soft)) return true;
  return false;
}

/**
 * Oraciones tipo: «agendar cita para el jueves» / «… para Ana de 2 a 3»
 */
export function parseNaturalSchedule(text, { pacientes, equipo } = {}) {
  const soft = softenVoiceText(text);
  if (!soft || soft.split(/\s+/).length < 2) return null;
  if (!hasScheduleIntent(soft) && !/\bpara\s+(la\s+)?(persona|paciente)\b/.test(soft)) {
    return null;
  }

  const patientPhrase = extractPatientPhrase(soft);
  const patient = patientPhrase ? matchPaciente(patientPhrase, pacientes) : null;
  const daySpoken = extractWeekdayDate(soft);
  const day = daySpoken || (() => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  })();
  const times = extractTimeRange(soft);
  const motivo = extractMotivo(soft);

  let idDoctorCita = null;
  const docM = soft.match(/\b(?:con|doctor|doctora|profesional)\s+(.+?)(?=\s+(?:el|la|un|de|para|motivo)|$)/);
  if (docM) idDoctorCita = matchEquipo(docM[1], equipo);

  const dayStr = toLocalDayString(day);
  const patch = {};
  const missing = [];

  if (patient) {
    patch.idPaciente = String(patient.id);
    patch.pacienteNombre = '';
    patch.pacientePendiente = false;
  } else if (patientPhrase) {
    const neat = titleCaseNombre(patientPhrase);
    patch.idPaciente = makePendingPacienteId(neat);
    patch.pacienteNombre = neat;
    patch.pacientePendiente = true;
  } else {
    missing.push('paciente');
  }

  if (times?.start) {
    patch.fechaHora = `${dayStr}T${times.start}`;
    patch.fechaHoraFin = `${dayStr}T${times.end || formatHm(toClinicHour(parseInt(times.start.slice(0, 2), 10) + 1), 0)}`;
  } else if (daySpoken) {
    // Día dicho sin hora: fija el día (09:00) y pide el horario.
    patch.fechaHora = `${dayStr}T09:00`;
    patch.fechaHoraFin = `${dayStr}T09:30`;
    missing.push('horario');
  } else {
    missing.push('horario');
  }
  if (motivo) patch.motivo = motivo;
  if (idDoctorCita) patch.idDoctorCita = idDoctorCita;

  const nombre = patient?.nombre || titleCaseNombre(patientPhrase) || 'paciente';
  const nuevoTag = !patient && patientPhrase ? ' (nuevo · pendiente de registrar)' : '';
  const resume = [
    patientPhrase || patient ? `${nombre}${nuevoTag}` : null,
    daySpoken ? formatDayEs(day) : null,
    times ? `${times.start}–${times.end}` : null,
    motivo || null,
  ].filter(Boolean).join(' · ');

  if (missing.length) {
    const tips = [];
    if (missing.includes('paciente')) tips.push('el nombre del paciente');
    if (missing.includes('horario')) tips.push('el horario (ej. de 2 a 3)');
    return {
      type: 'SCHEDULE_DRAFT',
      patch,
      needsConfirm: false,
      feedback: `Escuché: ${soft}. ${resume ? `Anoté ${resume}. ` : ''}Decime ${tips.join(' y ')}.`,
    };
  }

  return {
    type: 'SCHEDULE_DRAFT',
    patch,
    needsConfirm: true,
    summary: resume,
    feedback: `¿Está correcto agendar? ${resume}. Decí «sí» o «correcto».`,
  };
}

export function parseCitasVoiceChunk(text, ctx) {
  const raw = (text || '').trim();
  const soft = softenVoiceText(raw);
  const { modalOpen, pacientes, equipo, awaitingConfirm } = ctx;

  if (awaitingConfirm) {
    if (isConfirmYes(raw) || isConfirmYes(soft)) {
      return { type: 'SAVE', feedback: 'Perfecto, agendando…', clearConfirm: true };
    }
    if (isConfirmNo(raw) || isConfirmNo(soft)) {
      return { type: 'CLOSE_MODAL', feedback: 'Cancelado. No se agendó.', clearConfirm: true };
    }
  }

  const natural = parseNaturalSchedule(raw, { pacientes, equipo });
  if (natural) return natural;

  if (isOpenModalCommand(raw) || isOpenModalCommand(soft)) {
    return { type: 'OPEN_MODAL', feedback: 'Abriendo nueva cita…' };
  }

  if (isCloseCommand(raw) || isCloseCommand(soft)) {
    return { type: 'CLOSE_MODAL', feedback: 'Cerrando…', clearConfirm: true };
  }

  if (!modalOpen) {
    if (soft.length > 3) {
      return {
        type: 'STATUS',
        feedback: 'Probá: «agendar cita para Ana el viernes de 2 a 3».',
      };
    }
    return { type: 'NONE', feedback: null };
  }

  if (isSaveCommand(raw) || isSaveCommand(soft)) {
    return { type: 'SAVE', feedback: 'Guardando…', clearConfirm: true };
  }

  // Complementos cortos con el formulario abierto: «el jueves», «de 2 a 3»
  {
    const dayOnly = extractWeekdayDate(soft);
    const timesOnly = extractTimeRange(soft);
    const toksN = soft.split(/\s+/).length;

    if (dayOnly && !timesOnly && toksN <= 5) {
      const dayStr = toLocalDayString(dayOnly);
      return {
        type: 'PATCH',
        patch: {
          fechaHora: `${dayStr}T09:00`,
          fechaHoraFin: `${dayStr}T09:30`,
        },
        feedback: `Día: ${formatDayEs(dayOnly)}. Ahora decime horario y paciente.`,
      };
    }
    if (timesOnly && toksN <= 8) {
      if (dayOnly) {
        const dayStr = toLocalDayString(dayOnly);
        return {
          type: 'PATCH',
          patch: {
            fechaHora: `${dayStr}T${timesOnly.start}`,
            fechaHoraFin: `${dayStr}T${timesOnly.end}`,
          },
          feedback: `Horario ${timesOnly.start}–${timesOnly.end} (${formatDayEs(dayOnly)}).`,
        };
      }
      return {
        type: 'TIME_RANGE',
        start: timesOnly.start,
        end: timesOnly.end,
        feedback: `Horario ${timesOnly.start}–${timesOnly.end}.`,
      };
    }
  }

  let m = soft.match(/^(motivo|asunto)\s*(.+)$/);
  if (m) {
    const motivo = m[2].trim();
    return {
      type: 'PATCH',
      patch: { motivo },
      feedback: `Motivo: ${motivo.slice(0, 40)}${motivo.length > 40 ? '…' : ''}`,
    };
  }

  m = soft.match(/^notas?\s*(.+)$/);
  if (m) {
    return { type: 'PATCH', patch: { notas: m[1].trim() }, feedback: 'Notas actualizadas.' };
  }

  m = soft.match(/^paciente\s*(.+)$/);
  if (m) {
    const hit = matchPaciente(m[1], pacientes);
    if (hit) {
      return {
        type: 'PATCH',
        patch: { idPaciente: String(hit.id), pacienteNombre: '', pacientePendiente: false },
        feedback: `Paciente: ${hit.nombre}.`,
      };
    }
    const neat = titleCaseNombre(m[1]);
    return {
      type: 'PATCH',
      patch: {
        idPaciente: makePendingPacienteId(neat),
        pacienteNombre: neat,
        pacientePendiente: true,
      },
      feedback: `Paciente nuevo: ${neat} (pendiente de registrar).`,
    };
  }

  m = soft.match(/^(doctor|doctora|profesional)\s*(.+)$/);
  if (m && equipo?.length) {
    const id = matchEquipo(m[2], equipo);
    if (id) {
      return { type: 'PATCH', patch: { idDoctorCita: id }, feedback: 'Profesional seleccionado.' };
    }
    return { type: 'STATUS', feedback: 'No encontré ese profesional en el equipo.' };
  }

  if (/^(hora\s+)?inicio\b/.test(soft) || /^desde\b/.test(soft)) {
    const p = parseTimePair(soft);
    if (p) {
      const hh = String(toClinicHour(parseInt(p[0], 10), soft)).padStart(2, '0');
      const mm = String(Math.min(59, parseInt(p[1], 10))).padStart(2, '0');
      return { type: 'TIME_START', time: `${hh}:${mm}`, feedback: `Inicio ${hh}:${mm}` };
    }
  }

  if (/^(hora\s+)?fin\b/.test(soft) || /^hasta\b/.test(soft) || /^final\b/.test(soft)) {
    const p = parseTimePair(soft);
    if (p) {
      const hh = String(toClinicHour(parseInt(p[0], 10), soft)).padStart(2, '0');
      const mm = String(Math.min(59, parseInt(p[1], 10))).padStart(2, '0');
      return { type: 'TIME_END', time: `${hh}:${mm}`, feedback: `Fin ${hh}:${mm}` };
    }
  }

  if (pacientes?.length && soft.split(/\s+/).length <= 4 && soft.length > 2) {
    const hit = matchPaciente(soft, pacientes);
    if (hit && soft.length >= 3) {
      return { type: 'PATCH', patch: { idPaciente: String(hit.id) }, feedback: `Paciente: ${hit.nombre}.` };
    }
  }

  if (soft.length > 6) {
    return {
      type: 'STATUS',
      feedback: 'Ej.: «agendar cita para Ana el viernes de 2 a 3». Luego «sí» para confirmar.',
    };
  }

  return { type: 'NONE', feedback: null };
}

/**
 * Mantener pulsado para hablar → al soltar transcribe y ejecuta el comando.
 */
export function useCitasVoiceAssistant({
  active,
  modalOpen,
  pacientes,
  equipo,
  onOpenModal,
  onCloseModal,
  setForm,
  onSave,
  getFormDatePrefix,
}) {
  const [supported, setSupported] = useState(true);
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [lastHeard, setLastHeard] = useState('');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [backendReady, setBackendReady] = useState(false);
  const [backendHint, setBackendHint] = useState('');
  const [provider, setProvider] = useState(null);
  const [awaitingConfirm, setAwaitingConfirm] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState('');

  const recorderRef = useRef(null);
  const pressSeqRef = useRef(0);
  const busyRef = useRef(false);
  const awaitingConfirmRef = useRef(false);

  const ctxRef = useRef({
    modalOpen,
    pacientes,
    equipo,
    onOpenModal,
    onCloseModal,
    setForm,
    onSave,
    getFormDatePrefix,
  });
  ctxRef.current = {
    modalOpen,
    pacientes,
    equipo,
    onOpenModal,
    onCloseModal,
    setForm,
    onSave,
    getFormDatePrefix,
  };

  const dispatch = useCallback((chunk) => {
    const ctx = ctxRef.current;
    setLastHeard(chunk);
    const cmd = parseCitasVoiceChunk(chunk, {
      modalOpen: ctx.modalOpen,
      pacientes: ctx.pacientes,
      equipo: ctx.equipo,
      awaitingConfirm: awaitingConfirmRef.current,
    });

    if (cmd.feedback) setFeedback(cmd.feedback);
    else setFeedback('');

    if (cmd.clearConfirm || cmd.type === 'CLOSE_MODAL') {
      awaitingConfirmRef.current = false;
      setAwaitingConfirm(false);
      setConfirmSummary('');
    }

    const dayPrefix = () => {
      const prefix = ctx.getFormDatePrefix?.();
      return prefix || new Date().toISOString().slice(0, 10);
    };

    switch (cmd.type) {
      case 'OPEN_MODAL':
        ctx.onOpenModal?.();
        break;
      case 'SCHEDULE_DRAFT': {
        // Si ya hay formulario abierto, se acumulan los datos (día → paciente → hora).
        if (ctx.modalOpen) {
          ctx.setForm?.((f) => ({ ...f, ...(cmd.patch || {}) }));
        } else {
          ctx.onOpenModal?.(cmd.patch || {});
        }
        if (cmd.needsConfirm) {
          awaitingConfirmRef.current = true;
          setAwaitingConfirm(true);
          setConfirmSummary(cmd.summary || '');
        } else {
          awaitingConfirmRef.current = false;
          setAwaitingConfirm(false);
          setConfirmSummary('');
        }
        break;
      }
      case 'CLOSE_MODAL':
        if (ctx.modalOpen) ctx.onCloseModal?.();
        break;
      case 'SAVE':
        // No exigir modalOpen del render: puede atrasarse un tick tras SCHEDULE_DRAFT.
        ctx.onSave?.();
        awaitingConfirmRef.current = false;
        setAwaitingConfirm(false);
        setConfirmSummary('');
        break;
      case 'PATCH':
        ctx.setForm?.((f) => ({ ...f, ...cmd.patch }));
        break;
      case 'TIME_START': {
        const day = dayPrefix();
        ctx.setForm?.((f) => ({ ...f, fechaHora: `${day}T${cmd.time}` }));
        break;
      }
      case 'TIME_END': {
        const day = dayPrefix();
        ctx.setForm?.((f) => ({ ...f, fechaHoraFin: `${day}T${cmd.time}` }));
        break;
      }
      case 'TIME_RANGE': {
        const day = dayPrefix();
        ctx.setForm?.((f) => ({
          ...f,
          fechaHora: `${day}T${cmd.start}`,
          fechaHoraFin: `${day}T${cmd.end}`,
        }));
        break;
      }
      default:
        break;
    }

    return cmd;
  }, []);

  const runTextCommand = useCallback((text) => {
    const t = (text || '').trim();
    if (!t) return;
    setLiveTranscript(t);
    dispatch(t);
  }, [dispatch]);

  const confirmPending = useCallback(() => {
    if (!awaitingConfirmRef.current) return;
    dispatch('sí');
  }, [dispatch]);

  const rejectPending = useCallback(() => {
    if (!awaitingConfirmRef.current) return;
    dispatch('no');
  }, [dispatch]);

  const cancelRecorder = useCallback(() => {
    if (recorderRef.current) {
      try {
        recorderRef.current.cancel();
      } catch {
        /* noop */
      }
      recorderRef.current = null;
    }
  }, []);

  /** Mantener pulsado: empieza a grabar. */
  const pressTalk = useCallback(async () => {
    if (!active || busyRef.current || listening || transcribing) return;
    if (!backendReady) {
      setFeedback(
        backendHint
          || 'No hay motor Whisper en el servidor. Configurá DPMS_OPENAI_API_KEY o instalá faster-whisper.',
      );
      return;
    }

    const seq = pressSeqRef.current + 1;
    pressSeqRef.current = seq;
    busyRef.current = true;

    try {
      const rec = await startVoiceRecording({
        mode: 'hold',
        maxMs: 30000,
      });
      if (pressSeqRef.current !== seq) {
        rec.cancel();
        busyRef.current = false;
        return;
      }
      recorderRef.current = rec;
      setListening(true);
      setLiveTranscript('');
      setFeedback('Hablá ahora… soltá para procesar.');
    } catch (err) {
      setListening(false);
      setFeedback(
        err?.name === 'NotAllowedError'
          || /Permission|NotAllowed|denied/i.test(String(err?.message || err))
          ? 'Sin permiso de micrófono. Permitilo en el candado de la barra de direcciones.'
          : (err?.message || 'No se pudo iniciar la grabación.'),
      );
    } finally {
      busyRef.current = false;
    }
  }, [active, listening, transcribing, backendReady, backendHint]);

  /** Soltar: corta y transcribe. */
  const releaseTalk = useCallback(async () => {
    pressSeqRef.current += 1;

    const rec = recorderRef.current;
    if (!rec) {
      setListening(false);
      return;
    }
    recorderRef.current = null;
    setListening(false);
    setTranscribing(true);
    setFeedback('Cortando audio…');
    try {
      const blob = await rec.stop();
      if (!blob || blob.size < 250) {
        setFeedback('No escuché nada. Mantené pulsado, hablá y soltá.');
        return;
      }
      setFeedback('Reconociendo (Whisper)…');
      const ext = extensionForMime(blob.type);
      const { data } = await transcribeSpeechAudio(blob, `comando.${ext}`);
      const text = (data?.text || '').trim();
      setLiveTranscript(text);
      if (!text) {
        setFeedback('No entendí. Probá de nuevo manteniendo pulsado.');
      } else {
        dispatch(text);
      }
    } catch (err) {
      setFeedback(formatApiError(err, 'No se pudo transcribir el audio.'));
    } finally {
      setTranscribing(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (!active) {
      pressSeqRef.current += 1;
      cancelRecorder();
      busyRef.current = false;
      awaitingConfirmRef.current = false;
      setAwaitingConfirm(false);
      setConfirmSummary('');
      setListening(false);
      setTranscribing(false);
      setLiveTranscript('');
      setFeedback('');
      return undefined;
    }

    let cancelled = false;
    setFeedback('Cargando motor de voz del servidor…');
    getSpeechStatus()
      .then(({ data }) => {
        if (cancelled) return;
        const ok = !!data?.available;
        setBackendReady(ok);
        setProvider(data?.provider || null);
        setBackendHint(data?.hint || '');
        setSupported(ok || typeof MediaRecorder !== 'undefined');
        setFeedback(
          ok
            ? 'Listo. Decí p. ej.: «agendar cita para Ana el viernes de 2 a 3».'
            : (data?.hint
              || 'El servidor aún no tiene Whisper. Podés escribir comandos abajo.'),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setBackendReady(false);
        setProvider(null);
        setBackendHint(formatApiError(err, 'No se pudo consultar el estado de voz del servidor.'));
        setFeedback('No se pudo conectar al motor de voz del servidor. Usá el cuadro de texto.');
      });

    return () => {
      cancelled = true;
      pressSeqRef.current += 1;
      cancelRecorder();
    };
  }, [active, cancelRecorder]);

  return {
    supported,
    listening,
    transcribing,
    feedback,
    lastHeard,
    liveTranscript,
    backendReady,
    backendHint,
    provider,
    cloudBlocked: !backendReady,
    awaitingConfirm,
    confirmSummary,
    setFeedback,
    pressTalk,
    releaseTalk,
    startListening: pressTalk,
    stopListening: releaseTalk,
    runTextCommand,
    confirmPending,
    rejectPending,
  };
}
